import { Box, Chip, Stack } from '@mui/material'
import { get } from 'lodash'
import { ReactElement, ReactNode } from 'react'
import { Form } from 'react-final-form'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { CriteriaConfigTable } from 'src/screens/shared/application/assessment/details/CriteriaConfigTable'
import {
  CriteriaFormValues,
  CriteriaGroups,
  getAvgRatingFromCriteriaValues,
  QUALITY_PREFIX,
} from 'src/screens/shared/assessment-criteria/utils/AssessmentUtils'
import { EditButton } from 'src/shared/button/Buttons'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { DefaultSectionTypography } from 'src/shared/presentation/DefaultSectionTypography'
import { HelpAndInstructions } from 'src/shared/presentation/HelpAndInstructions'
import { Section } from 'src/shared/presentation/Section'
import { useUserLocale } from 'src/user/UserContext'
import styled from 'styled-components'

const StyledChip = styled(Chip)`
  min-width: 50px;
`

interface Props {
  screenLayoutTitle: string
  onBackHandler: () => void
  actions?: ReactElement
  sectionLayoutTitle: string
  helpText: string
  criteriaGroups: CriteriaGroups | undefined
  initialValues: CriteriaFormValues | undefined
  canEdit: boolean
  onEditHandler: () => void
  loading: boolean
  optional?: boolean
  commentSection?: ReactNode
  showRating?: boolean
}

export const BaseAssessmentCriteria = ({
  screenLayoutTitle,
  onBackHandler,
  actions,
  sectionLayoutTitle,
  helpText,
  criteriaGroups,
  initialValues,
  canEdit,
  onEditHandler,
  loading,
  optional,
  commentSection,
  showRating = false,
}: Props) => {
  const { getMessage } = useMessageSource()

  const language = useUserLocale()
  const hasCriteriaGroups = criteriaGroups !== undefined && Object.keys(criteriaGroups).length != 0

  return (
    <ScreenLayout title={screenLayoutTitle} onBack={onBackHandler} actions={actions}>
      {!loading && criteriaGroups !== undefined && (
        <PageLayout>
          <Section
            id="assessment-criteria"
            title={sectionLayoutTitle}
            actionButton={
              hasCriteriaGroups && (
                <EditButton origin="content" color="primary" hidden={!canEdit} onClick={onEditHandler} />
              )
            }
            helpAndInstructions={<HelpAndInstructions labelKey={helpText} />}
            optional={optional}
          >
            <Box mt={2}>
              {hasCriteriaGroups ? (
                <Form
                  initialValues={initialValues}
                  onSubmit={() => {}}
                  render={({ handleSubmit, values }) => {
                    return (
                      <form onSubmit={handleSubmit} noValidate>
                        <Stack spacing={5}>
                          {Object.values(criteriaGroups || {}).map((criteriaSelections) => (
                            <CriteriaConfigTable
                              key={criteriaSelections[0].id}
                              rows={criteriaSelections}
                              groupTitle={get(criteriaSelections[0].cgNames, language, '')}
                              groupAvgRating={
                                showRating
                                  ? getAvgRatingFromCriteriaValues(criteriaSelections, initialValues)
                                  : undefined
                              }
                              mode="edit" // 2021-06-12 ABF: Just to make chip not disabled
                              EditControl={StyledChip}
                              resolveControlProps={(row) => ({
                                name: `${QUALITY_PREFIX}${row.id}`,
                                label: values[`${QUALITY_PREFIX}${row.id}`] || getMessage('label.field.not.available'),
                              })}
                            />
                          ))}
                        </Stack>
                      </form>
                    )
                  }}
                />
              ) : (
                <DefaultSectionTypography type="SELECTION" $standAlone={true} />
              )}
            </Box>
          </Section>
          {commentSection}
        </PageLayout>
      )}
    </ScreenLayout>
  )
}
